import type { ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router-dom';

import { ApiError, type ReviewRequirement, type SceneValidationRow } from '../../lib/api.js';
import { Icon, type IconName } from '../Icon.js';
import { RequirementStatusTag, RequirementValues, type SceneCardRequirement } from '../SceneCard.js';
import { StatusPill, type WorkState } from '../ui/StatusPill.js';

import '../../styles/breakdown.css';

/** What the scene pane needs of the requirements query: the shared `['requirements', projectId]` read. */
export interface RequirementsRead {
  readonly data: readonly ReviewRequirement[] | undefined;
  readonly error: unknown;
  readonly isPending: boolean;
  readonly refetch: () => unknown;
}

/**
 * The requirements one scene is linked to (`requirement_scene`, as the API lists
 * each requirement's scenes): the ones in play, and how many were marked not
 * needed. A merged one (`SUPERSEDED`) is neither — its scenes went to the one it
 * joined. Pure, so the rule is asserted without a screen.
 */
export function requirementsOfScene(
  all: readonly ReviewRequirement[],
  sceneId: string,
): { inPlay: ReviewRequirement[]; notNeeded: number } {
  const linked = all.filter((r) => r.scenes.some((s) => s.sceneId === sceneId));

  return {
    inPlay: linked.filter((r) => r.status !== 'DISMISSED' && r.status !== 'SUPERSEDED'),
    notNeeded: linked.filter((r) => r.status === 'DISMISSED').length,
  };
}

/** A requirement as the requirements screen's cards draw it, so both say the same thing the same way. */
function asCard(requirement: ReviewRequirement): SceneCardRequirement {
  return {
    id: requirement.id,
    title: requirement.title,
    status: requirement.status,
    // READ DEFENSIVELY, as the requirements screen does: `facet` is on every
    // constraint row the server sends, and absent from an older API's answer.
    constraints: requirement.constraints.map((c) => {
      const facet: unknown = (c as { facet?: unknown }).facet;

      return { id: c.id, kind: c.kind, facet: typeof facet === 'string' ? facet : null, label: c.label };
    }),
    creativeDescription: requirement.creativeDescription,
  };
}

/**
 * LOCATION REQUIREMENTS, ON THE SCENE — what a location for this scene must be
 * like, read where the scene is reviewed (owner request of 2026-11-04, from the
 * Script & Scenes mockup).
 *
 * READ-ONLY. Approving, setting aside, editing the values and adding a
 * requirement stay in the location breakdown, which the link opens on this
 * scene; this block only says what is there. Its values are the requirements
 * screen's own chips (`RequirementValues`), grouped by the question each answers
 * and marked Must / Nice / Not.
 *
 * EMPTY IS TWO DIFFERENT FACTS, and the block never confuses them: the AI has
 * not read this scene yet (nothing to say about the screenplay), or it read the
 * scene and suggested nothing. A role that may not read requirements sees no
 * block at all, never an empty one.
 *
 * The header says whether the AI has read the scene (Not analysed, Analysed, Out
 * of date) from the scene row's own `analysis`; the block offers no AI run of its
 * own (step 2 has none since 2026-10-29) and no way to add one here.
 */
export function SceneLocationRequirements({
  projectId,
  scene,
  read,
}: {
  projectId: string;
  scene: SceneValidationRow;
  read: RequirementsRead;
}) {
  const routed = useInRouterContext();
  if (read.error instanceof ApiError && read.error.status === 403) return null;

  const href = `/projects/${projectId}/breakdown?scene=${encodeURIComponent(scene.id)}`;
  const link = (
    <>
      Review requirements
      <Icon name="arrowRight" size={13} />
    </>
  );

  let body: ReactNode;
  // The location breakdown opens once the scene list is confirmed; before the
  // AI has read the scene or anything is linked to it, there is nothing there
  // for this scene, so no link points at it.
  let linked = false;

  if (read.isPending) {
    body = (
      <p className="sw-hint" role="status" data-testid="scene-panel-locations-loading">
        Loading location requirements…
      </p>
    );
  } else if (read.error !== null && read.error !== undefined) {
    body = (
      <p className="sw-hint" data-testid="scene-panel-locations-error">
        Location requirements could not be loaded.{' '}
        <button
          type="button"
          className="ctl"
          data-testid="scene-panel-locations-retry"
          onClick={() => {
            void read.refetch();
          }}
        >
          Try again
        </button>
      </p>
    );
  } else {
    const { inPlay, notNeeded } = requirementsOfScene(read.data ?? [], scene.id);
    linked = inPlay.length > 0 || notNeeded > 0 || scene.analysis !== 'NOT_ANALYSED';

    if (inPlay.length > 0) {
      body = (
        <>
          {scene.analysis === 'OUT_OF_DATE' ? (
            <p className="sw-hint sw-locreq-note" data-testid="scene-panel-locations-out-of-date">
              Suggested before this scene changed. Check that they still fit it.
            </p>
          ) : null}
          <ul className="sw-locreq-list">
            {inPlay.map((requirement) => (
              <RequirementLine key={requirement.id} requirement={requirement} sceneId={scene.id} />
            ))}
          </ul>
        </>
      );
    } else if (notNeeded > 0) {
      body = (
        <Empty title="Marked not needed">
          <p className="sw-hint" data-testid="scene-panel-locations-not-needed">
            {notNeeded === 1
              ? 'Its location requirement was marked not needed.'
              : 'Its location requirements were marked not needed.'}
          </p>
        </Empty>
      );
    } else if (scene.analysis !== 'NOT_ANALYSED') {
      body = (
        <Empty title="Nothing suggested for this scene">
          <p className="sw-hint" data-testid="scene-panel-locations-none">
            The AI analysis suggested no location requirement for this scene. You can add one in the location breakdown.
          </p>
        </Empty>
      );
    } else {
      body = (
        <Empty title="No requirements yet">
          <p className="sw-hint" data-testid="scene-panel-locations-not-read">
            None yet. They come from the AI analysis, or you add them by hand, in the location breakdown.
          </p>
        </Empty>
      );
    }
  }

  const reading = READING[scene.analysis];

  return (
    <section className="sw-detail-section sw-locreq" aria-labelledby={`scene-locations-${scene.id}`} data-testid="scene-panel-locations">
      <div className="sw-locreq-head">
        <span className="sw-section-icon" aria-hidden="true">
          <Icon name="pin" size={20} />
        </span>
        <div className="sw-locreq-title">
          <h4 className="sw-section-title" id={`scene-locations-${scene.id}`}>
            Location requirements
          </h4>
          <p className="sw-hint sw-locreq-sub">Key elements we’ll look for in locations</p>
        </div>
        <div className="sw-locreq-aside">
          {/* Whether the AI has read this scene: the scene row's own state, never a count. */}
          <StatusPill state={reading.state} label={reading.label} icon={reading.icon} size="sm" testId="scene-panel-locations-reading" />
          {linked ? (
            routed ? (
              <Link to={href} className="cc-link" data-testid="scene-panel-locations-open">
                {link}
              </Link>
            ) : (
              <a href={href} className="cc-link" data-testid="scene-panel-locations-open">
                {link}
              </a>
            )
          ) : null}
        </div>
      </div>
      {body}
    </section>
  );
}

/** The scene's AI reading in the workspace's status words (`SceneAnalysisState`). */
const READING: Record<SceneValidationRow['analysis'], { state: WorkState; label: string; icon: IconName }> = {
  NOT_ANALYSED: { state: 'NOT_STARTED', label: 'Not analysed', icon: 'circleDashed' },
  CURRENT: { state: 'COMPLETED', label: 'Analysed', icon: 'sparkle' },
  OUT_OF_DATE: { state: 'OUTDATED', label: 'Out of date', icon: 'clock' },
};

/** Nothing to list: a title saying which kind of nothing, and the sentence that explains it. */
function Empty({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="sw-locreq-empty">
      <span className="sw-locreq-empty-icon" aria-hidden="true">
        <Icon name="map" size={22} />
      </span>
      <div>
        <p className="sw-locreq-empty-title">{title}</p>
        {children}
      </div>
    </div>
  );
}

function RequirementLine({ requirement, sceneId }: { requirement: ReviewRequirement; sceneId: string }) {
  const card = asCard(requirement);
  const others = requirement.scenes
    .filter((s) => s.sceneId !== sceneId)
    .map((s) => s.sceneNumber ?? (s.ordinal === null ? null : String(s.ordinal)))
    .filter((label): label is string => label !== null);
  const hasValues = (card.constraints?.length ?? 0) > 0 || (card.creativeDescription ?? '').trim() !== '';

  return (
    <li className="sw-locreq-item" data-testid={`scene-panel-location-${requirement.id}`}>
      <p className="sw-locreq-name">
        <strong>{requirement.title}</strong>
        <RequirementStatusTag status={requirement.status} />
      </p>
      {others.length === 0 ? null : (
        <p className="sw-hint" data-testid={`scene-panel-location-${requirement.id}-shared`}>
          {`Also in ${others.length === 1 ? 'scene' : 'scenes'} ${others.join(', ')}`}
        </p>
      )}
      {hasValues ? (
        <RequirementValues requirement={card} />
      ) : (
        <p className="sw-hint" data-testid={`scene-panel-location-${requirement.id}-no-values`}>
          No values set yet.
        </p>
      )}
    </li>
  );
}
